import { Button, Card, Icon, Meter, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney } from '@/lib/money'
import dash from '@/features/dashboard/DashboardScreen.module.css'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const MONTHLY = [
  ['Oct', 2.1], ['Nov', 2.8], ['Dec', 3.4], ['Jan', 3.9], ['Feb', 3.2], ['Mar', 4.1],
  ['Apr', 4.4], ['May', 5.2], ['Jun', 4.8], ['Jul', 5.6], ['Aug', 6.1], ['Sep', 1.56],
] as const

const OFFERS = [
  { brand: 'Soriana', detail: 'Groceries · until 30 Sep', rate: '5%' },
  { brand: 'Claro', detail: 'Phone top-up · until 15 Oct', rate: '4%' },
  { brand: 'Farmacias del Ahorro', detail: 'Pharmacy · until 31 Oct', rate: '3%' },
]

const HOW = [
  { icon: 'percent', title: '0.5% on everything', body: 'No categories to remember and nothing to rotate, up to $600 a month.' },
  { icon: 'payments', title: 'Paid in cash, not points', body: 'It lands in your USD account on the 1st and stays there earning.' },
  { icon: 'block', title: "What doesn't earn", body: 'ATMs, transfers between your own accounts, crypto and gambling. Refunds are deducted.' },
]

export function CashbackScreen() {
  const repository = useRepository()
  const transactions = useQuery(() => repository.listTransactions(), [repository])
  const peak = Math.max(...MONTHLY.map(([, value]) => value))
  const earning = transactions.data?.filter((row) => row.cashback) ?? []

  return (
    <>
      <PageHeader
        title="Cashback"
        subtitle="0.5% on everything you buy, up to $600 a month. Paid in cash, not points."
        actions={<Button variant="ghost" size="md" iconStart="receipt_long" to="/transactions">Statements</Button>}
      />

      <BentoGrid>
        <BentoSpan span={3}>
          <Card tone="dark">
            <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">Earned this year</Text>
            <Stack direction="row" justify="between" align="end" className="pb-mt-2" gap={4}>
              <Text as="p" variant="display" weight="bold" tone="accent" numeric>$47.20</Text>
              <Text variant="caption" tone="onDarkMuted">Paid on the 1st of each month, into your USD account</Text>
            </Stack>
            <div className="pb-mt-5">
              <div className={dash.chart}>
                {MONTHLY.map(([label, value], index) => (
                  <div key={label} className={dash.column}>
                    <div
                      className={`${dash.bar} ${dash.barIn}`}
                      style={{
                        height: `${(value / peak) * 100}%`,
                        opacity: index === MONTHLY.length - 1 ? 0.45 : 1,
                      }}
                      title={`${label} · $${value.toFixed(2)}`}
                    />
                  </div>
                ))}
              </div>
              <div className={dash.axis}>
                {MONTHLY.map(([label]) => (
                  <Text key={label} variant="eyebrow" tone="onDarkMuted" className={dash.axisLabel}>{label}</Text>
                ))}
              </div>
            </div>
          </Card>
        </BentoSpan>

        <Card tone="light">
          <Card.Header>
            <Text variant="caption" weight="semibold">This month</Text>
            <Text variant="caption" tone="secondary" numeric>$312 of $600</Text>
          </Card.Header>
          <Stack gap={3} className="pb-mt-4">
            <Meter value={312 / 600} height={10} label="Monthly cap used" />
            <Stack direction="row" justify="between" align="center">
              <Text variant="caption" tone="secondary">Earned so far</Text>
              <Text variant="caption" weight="semibold" tone="positive" numeric>$1.56</Text>
            </Stack>
            <Text variant="caption" tone="secondary">
              At this rate you&rsquo;ll finish the month around $3.00. The cap resets on the 1st.
            </Text>
          </Stack>
        </Card>

        <BentoSpan span={2}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">What earned this month</Text>
              <Text variant="eyebrow" tone="secondary">Refunds are taken back off</Text>
            </Card.Header>
            <Stack gap={0} className="pb-mt-3">
              {earning.map((row) => (
                <div key={row.id} className={checkout.line}>
                  <Icon name={row.icon} size={18} />
                  <Stack grow gap={0}>
                    <Text variant="caption" weight="semibold" truncate>{row.counterparty}</Text>
                    <Text variant="eyebrow" tone="secondary">{row.category}</Text>
                  </Stack>
                  <Text variant="caption" numeric tone="secondary">{formatMoney(row.amount)}</Text>
                  <Text variant="caption" weight="semibold" tone="positive" numeric style={{ width: '4.5rem', textAlign: 'right' }}>
                    {row.cashback ? formatMoney(row.cashback, { signed: true }) : ''}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Stack gap={3}>
            <Card tone="dark">
              <Card.Header>
                <Text variant="caption" weight="semibold" tone="onDark">Offers near you</Text>
                <Text variant="eyebrow" tone="onDarkMuted">Paid by the shop</Text>
              </Card.Header>
              <Text variant="caption" tone="onDarkMuted" className="pb-mt-2">
                These sit on top of your 0.5%. The shop pays for them, so they don&rsquo;t count
                against your cap.
              </Text>
              <Stack gap={3} className="pb-mt-4">
                {OFFERS.map((offer) => (
                  <Stack key={offer.brand} direction="row" align="center" gap={3}>
                    <Card tone="accent" padded={false} className="pb-offer-rate">
                      <Text variant="caption" weight="bold">{offer.rate}</Text>
                    </Card>
                    <Stack grow gap={0}>
                      <Text variant="caption" weight="semibold" tone="onDark" truncate>{offer.brand}</Text>
                      <Text variant="eyebrow" tone="onDarkMuted" truncate>{offer.detail}</Text>
                    </Stack>
                    <Button variant="accentDim" size="sm">Activate</Button>
                  </Stack>
                ))}
              </Stack>
            </Card>

            <Card tone="plain">
              <Text variant="caption" weight="semibold">How it works</Text>
              <div className={`${checkout.reasons} pb-mt-2`}>
                {HOW.map((item) => (
                  <div key={item.title} className={checkout.reason}>
                    <Icon name={item.icon} size={18} />
                    <Stack gap={1}>
                      <Text variant="caption" weight="semibold">{item.title}</Text>
                      <Text variant="caption" tone="secondary">{item.body}</Text>
                    </Stack>
                  </div>
                ))}
              </div>
            </Card>
          </Stack>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
